import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip as MuiTooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { BrandSpinner } from "../../../components/feedback/BrandSpinner";
import { SoftDataGrid } from "../../../components/data/SoftDataGrid";
import { getActivity } from "../../../services/activityService";
import { getCurrencies } from "../../../services/currenciesService";
import { getShops } from "../../../services/shopsService";
import { getMonthToDateRange } from "../../../utils/localDate";
import { AnalyticsFiltersDrawer } from "../../analytics/components/AnalyticsFiltersDrawer";

const chartColors = [
  "#FF8A65",
  "#4DB6AC",
  "#64B5F6",
  "#BA68C8",
  "#FFD54F",
  "#81C784",
  "#F06292",
  "#7986CB",
];

function extract(payload, key) {
  const paginator = payload?.[key] ?? payload?.data?.[key] ?? payload?.data ?? payload;
  return Array.isArray(paginator?.data)
    ? paginator.data
    : Array.isArray(paginator)
      ? paginator
      : [];
}

function formatDateTime(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-ZA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ActivityPage() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filters, setFilters] = useState(() => ({
    ...getMonthToDateRange(),
    shop_ids: [],
    currencies: [],
    group_by: "day",
  }));

  const shopsQuery = useQuery({
    queryKey: ["activity-shop-options"],
    queryFn: () => getShops({ page: 1, per_page: 100, is_active: 1 }),
  });

  const currenciesQuery = useQuery({
    queryKey: ["activity-currency-options"],
    queryFn: () => getCurrencies({ page: 1, per_page: 100, is_active: 1 }),
  });

  const shops = useMemo(() => extract(shopsQuery.data, "shops"), [shopsQuery.data]);
  const currencies = useMemo(
    () => extract(currenciesQuery.data, "currencies"),
    [currenciesQuery.data],
  );

  useEffect(() => {
    if (shops.length && filters.shop_ids.length === 0) {
      setFilters((current) => ({
        ...current,
        shop_ids: shops.map((shop) => shop.id),
      }));
    }
  }, [shops, filters.shop_ids.length]);

  useEffect(() => {
    if (currencies.length && filters.currencies.length === 0) {
      setFilters((current) => ({
        ...current,
        currencies: currencies.map((currency) => currency.code),
      }));
    }
  }, [currencies, filters.currencies.length]);

  const activityQuery = useQuery({
    queryKey: ["activity", filters],
    queryFn: () => getActivity(filters),
    enabled: Boolean(
      filters.from_date &&
        filters.to_date &&
        filters.shop_ids.length &&
        filters.currencies.length,
    ),
    refetchInterval: 60000,
  });

  const data = activityQuery.data || {};

  const trend = useMemo(() => {
    const map = new Map();
    (data.trend || []).forEach((row) => {
      const item = map.get(row.period) || { period: row.period };
      item[`${row.currency}_revenue`] = Number(row.revenue || 0);
      item[`${row.currency}_vouchers`] = Number(row.vouchers || 0);
      map.set(row.period, item);
    });
    return Array.from(map.values());
  }, [data.trend]);

  const receiptCountByCurrency = useMemo(
    () =>
      (data.currency_breakdown || []).map((row) => ({
        currency: row.currency,
        receipts: Number(row.vouchers || 0),
      })),
    [data.currency_breakdown],
  );

  const columns = useMemo(
    () => [
      {
        field: "shop_name",
        headerName: "Shop",
        minWidth: 210,
        flex: 1,
        valueGetter: (_, row) => row.shop?.shop_name || "—",
        renderCell: (params) => (
          <Box>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 800 }}>
              {params.row.shop?.shop_name || "—"}
            </Typography>
            <Typography sx={{ color: "#667085", fontSize: "0.61rem" }}>
              {params.row.shop?.shop_code || "—"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "receipt_number",
        headerName: "Receipt",
        minWidth: 165,
        flex: 0.8,
        valueGetter: (_, row) => row.voucher_request?.receipt_number || "—",
      },
      {
        field: "code",
        headerName: "Voucher code",
        minWidth: 190,
        flex: 0.9,
      },
      {
        field: "package_name",
        headerName: "Package",
        minWidth: 170,
        flex: 0.8,
        valueGetter: (_, row) => row.wifi_package?.name || "—",
      },
      {
        field: "amount_display",
        headerName: "Amount",
        minWidth: 120,
        valueGetter: (_, row) => `${row.currency} ${Number(row.amount || 0).toFixed(2)}`,
      },
      {
        field: "status",
        headerName: "Status",
        width: 105,
        renderCell: (params) => (
          <Chip
            label={params.row.status || "unknown"}
            size="small"
            sx={{
              height: 22,
              borderRadius: 0,
              textTransform: "capitalize",
              fontSize: "0.63rem",
              fontWeight: 750,
            }}
          />
        ),
      },
      {
        field: "issued_at",
        headerName: "Issued",
        minWidth: 165,
        valueFormatter: (value) => formatDateTime(value),
      },
    ],
    [],
  );

  if (
    activityQuery.isLoading ||
    shopsQuery.isLoading ||
    currenciesQuery.isLoading
  ) {
    return (
      <Box sx={{ minHeight: 420, display: "grid", placeItems: "center" }}>
        <BrandSpinner />
      </Box>
    );
  }

  const resetFilters = () =>
    setFilters({
      ...getMonthToDateRange(),
      shop_ids: shops.map((shop) => shop.id),
      currencies: currencies.map((currency) => currency.code),
      group_by: "day",
    });

  return (
    <Box sx={{ p: { xs: 1.2, md: 2 }, bgcolor: "#F7F8FA", minHeight: "100%" }}>
      <Stack direction="row" justifyContent="flex-end" spacing={0.4} sx={{ mb: 1.2 }}>
        <MuiTooltip title="Refresh activity">
          <IconButton
            size="small"
            onClick={() => activityQuery.refetch()}
            sx={{ border: "1px solid #DDE3EA", borderRadius: 0, bgcolor: "#FFFFFF" }}
          >
            <RefreshRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </MuiTooltip>

        <MuiTooltip title="Activity filters">
          <IconButton
            size="small"
            onClick={() => setDrawerOpen(true)}
            sx={{ border: "1px solid #DDE3EA", borderRadius: 0, bgcolor: "#FFFFFF" }}
          >
            <FilterAltOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </MuiTooltip>
      </Stack>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 1.2 }}>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 0 }}><Typography sx={{ color: "#667085", fontSize: "0.66rem" }}>POS requests</Typography><Typography sx={{ mt: 0.5, fontSize: "1.5rem", fontWeight: 900 }}>{data.request_summary?.total || 0}</Typography></Paper>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 0 }}><Typography sx={{ color: "#667085", fontSize: "0.66rem" }}>Successful</Typography><Typography sx={{ mt: 0.5, fontSize: "1.5rem", fontWeight: 900 }}>{data.request_summary?.successful || 0}</Typography></Paper>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 0 }}><Typography sx={{ color: "#667085", fontSize: "0.66rem" }}>Failed</Typography><Typography sx={{ mt: 0.5, fontSize: "1.5rem", fontWeight: 900 }}>{data.request_summary?.failed || 0}</Typography></Paper>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 0 }}><Typography sx={{ color: "#667085", fontSize: "0.66rem" }}>Recent activity</Typography><Typography sx={{ mt: 0.5, fontSize: "1.5rem", fontWeight: 900 }}>{data.recent?.length || 0}</Typography></Paper>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 1.2, mt: 1.2 }}>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 0, minHeight: 350 }}>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 850 }}>Revenue activity</Typography>
          <Box sx={{ height: 290, mt: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAECF0" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                {filters.currencies.map((currency, index) => (
                  <Line
                    key={currency}
                    type="monotone"
                    dataKey={`${currency}_revenue`}
                    name={`${currency} revenue`}
                    stroke={chartColors[index % chartColors.length]}
                    strokeWidth={3}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 0, minHeight: 350 }}>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 850 }}>Voucher headcount movement</Typography>
          <Box sx={{ height: 290, mt: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAECF0" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                {filters.currencies.map((currency, index) => (
                  <Bar
                    key={currency}
                    dataKey={`${currency}_vouchers`}
                    name={`${currency} vouchers`}
                    fill={chartColors[index % chartColors.length]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Box>




      <Paper variant="outlined" sx={{ borderRadius: 0, mt: 1.2, overflow: "hidden" }}>
        <Box sx={{ p: 1.2, borderBottom: "1px solid #E3E7EC" }}>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 850 }}>Recent POS voucher activity</Typography>
        </Box>
        <SoftDataGrid
          rows={data.recent || []}
          columns={columns}
          loading={activityQuery.isFetching}
          getRowId={(row) => row.id}
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { page: 0, pageSize: 10 } } }}
        />
      </Paper>

      <AnalyticsFiltersDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        filters={filters}
        setFilters={setFilters}
        shops={shops}
        currencies={currencies}
        onApply={() => activityQuery.refetch()}
        onReset={resetFilters}
      />
    </Box>
  );
}
